import React from 'react';

import { Icon } from '@chakra-ui/react';
import {
  MdAdd,
  MdBarChart,
  MdDescription,
  MdEventNote,
  MdList,
  MdLock,
  MdPeople,
  MdPerson,
  MdReceipt,
  MdSearch,
  MdSettings,
  MdStar,
} from 'react-icons/md';
import { FaCreditCard, FaTruck, FaUsersCog } from 'react-icons/fa';
import { PiTruckTrailer } from 'react-icons/pi';

// Auth
import SignInCentered from 'domains/auth/pages/signIn';
import ForgotPassword from 'domains/auth/pages/signIn/ForgotPassword';

// User
import UserProfileScreen from 'domains/user/pages/profileScreen';
import FleetScreen from 'domains/user/pages/fleet';

// Collaboration
import CreateReviewPage from 'domains/collaboration/pages/CreateReviewPage';

// Invoices
import MyInvoices from 'domains/invoice/pages/MyInvoices';

// Dashboards
import ClientDashboardPage from 'domains/shared/pages/dashboard/ClientDashboardPage';
import PartnerDashboardPage from 'domains/shared/pages/dashboard/PartnerDashboardPage';
import RoleDashboardRedirectPage from 'domains/shared/pages/dashboard/RoleDashboardRedirectPage';

// Listing domain
import CreateListing from 'domains/listing/pages/CreateListing';
import MyListings from 'domains/listing/pages/MyListings';
import ClientListingDetails from 'domains/listing/pages/ClientListingDetails';
import SearchListings from 'domains/listing/pages/SearchListings';
import PartnerListingDetails from 'domains/listing/pages/PartnerListingDetails';
import MyQuotes from 'domains/listing/pages/MyQuotes';
import PartnerCompletedListings from 'domains/listing/pages/PartnerCompletedListings';
// Admin pages
import AdminDashboard from 'domains/admin/pages/AdminDashboard';
import AdminListingDetails from 'domains/admin/pages/AdminListingDetails';
import AdminListings from 'domains/admin/pages/AdminListings';
import AdminPartners from 'domains/admin/pages/AdminPartners';
import AdminUsersPage from 'domains/admin/pages/AdminUsersPage';
import AdminAllTrucksPage from 'domains/admin/pages/AdminAllTrucksPage';
import AdminPayments from 'domains/admin/pages/AdminPayments';
import AdminInvoicesPage from 'domains/admin/pages/AdminInvoicesPage';
import AdminPurchaseTermsPage from 'domains/admin/pages/AdminPurchaseTermsPage';
import AdminReviews from 'domains/admin/pages/AdminReviews';
import AdminSettings from 'domains/admin/pages/AdminSettings';
import { IoCheckmarkDoneSharp } from "react-icons/io5";

const routes = [
  // ── Auth ─────────────────────────────────────────
  {
    name: 'nav.signIn',
    layout: '/auth',
    path: '/sign-in',
    icon: <Icon as={MdLock} w="20px" h="20px" />,
    component: <SignInCentered />,
  },
  {
    name: 'nav.forgotPassword',
    layout: '/auth',
    path: '/forgot-password',
    icon: <Icon as={MdLock} w="20px" h="20px" />,
    component: <ForgotPassword />,
  },

  // ── Universal ────────────────────────────────────
  {
    name: 'nav.dashboard',
    layout: '/',
    path: '/performance',
    icon: <Icon as={MdBarChart} w="20px" h="20px" />,
    component: <RoleDashboardRedirectPage />,
    roles: ['partner', 'client', 'admin'],
    sidebar: false,
  },
  {
    name: 'nav.profile',
    layout: '/',
    path: '/profile',
    icon: <Icon as={MdPerson} w="20px" h="20px" />,
    component: <UserProfileScreen />,
    roles: ['partner', 'client', 'admin'],
    sidebar: false,
  },
  {
    name: 'nav.createReview',
    layout: '/',
    path: '/reviews/create/:listingId',
    icon: <Icon as={MdStar} width="20px" height="20px" color="inherit" />,
    component: <CreateReviewPage />,
    roles: ['client'],
    sidebar: false,
  },

  // ── Client ───────────────────────────────────────
  {
    name: 'nav.dashboard',
    layout: '/',
    path: '/dashboard/client',
    icon: <Icon as={MdBarChart} w="20px" h="20px" />,
    component: <ClientDashboardPage />,
    roles: ['client'],
  },
  {
    name: 'nav.createListing',
    layout: '/',
    path: '/create-listing',
    icon: <Icon as={MdAdd} w="20px" h="20px" />,
    component: <CreateListing />,
    roles: ['client'],
  },
  {
    name: 'nav.myListings',
    layout: '/',
    path: '/my-listings',
    icon: <Icon as={MdList} w="20px" h="20px" />,
    component: <MyListings />,
    roles: ['client'],
  },
  {
    name: 'nav.listingDetails',
    layout: '/',
    path: '/my-listings/:id',
    component: <ClientListingDetails />,
    roles: ['client'],
    sidebar: false,
  },

  // ── Partner ──────────────────────────────────────
  {
    name: 'nav.dashboard',
    layout: '/',
    path: '/dashboard/partner',
    icon: <Icon as={MdBarChart} w="20px" h="20px" />,
    component: <PartnerDashboardPage />,
    roles: ['partner'],
  },
  {
    name: 'nav.searchListings',
    layout: '/',
    path: '/search',
    icon: <Icon as={MdSearch} w="20px" h="20px" />,
    component: <SearchListings />,
    roles: ['partner'],
  },
  {
    name: 'nav.completedListings',
    layout: '/',
    path: '/completed-listings',
    icon: <Icon as={IoCheckmarkDoneSharp} w="20px" h="20px" />,
    component: <PartnerCompletedListings />,
    roles: ['partner'],
  },
  {
    name: 'nav.listingDetails',
    layout: '/',
    path: '/listings/:id',
    component: <PartnerListingDetails />,
    roles: ['partner'],
    sidebar: false,
  },
  {
    name: 'nav.myQuotes',
    layout: '/',
    path: '/my-quotes',
    icon: <Icon as={MdEventNote} w="20px" h="20px" />,
    component: <MyQuotes />,
    roles: ['partner'],
  },
  {
    name: 'nav.manageFleet',
    layout: '/',
    path: '/fleet',
    icon: <Icon as={PiTruckTrailer} w="20px" h="20px" />,
    component: <FleetScreen />,
    roles: ['partner'],
  },
  {
    name: 'nav.myInvoices',
    layout: '/',
    path: '/my-invoices',
    icon: <Icon as={FaCreditCard} w="20px" h="20px" />,
    component: <MyInvoices />,
    roles: ['partner'],
  },

  // ── Admin ────────────────────────────────────────
  {
    name: 'nav.adminDashboard',
    layout: '/',
    path: '/admin/dashboard',
    icon: <Icon as={MdBarChart} w="20px" h="20px" />,
    component: <AdminDashboard />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminListings',
    layout: '/',
    path: '/admin/listings',
    icon: <Icon as={MdList} w="20px" h="20px" />,
    component: <AdminListings />,
    roles: ['admin'],
  },
  {
    name: 'nav.listingDetails',
    layout: '/',
    path: '/admin/listings/:id',
    component: <AdminListingDetails />,
    roles: ['admin'],
    sidebar: false,
  },
  {
    name: 'nav.adminPartners',
    layout: '/',
    path: '/admin/partners',
    icon: <Icon as={MdPeople} w="20px" h="20px" />,
    component: <AdminPartners />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminUsers',
    layout: '/',
    path: '/admin/manage-all-users',
    icon: <Icon as={FaUsersCog} w="20px" h="20px" />,
    component: <AdminUsersPage />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminTrucks',
    layout: '/',
    path: '/admin/all-trucks',
    icon: <Icon as={FaTruck} w="20px" h="20px" />,
    component: <AdminAllTrucksPage />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminPayments',
    layout: '/',
    path: '/admin/payments',
    icon: <Icon as={FaCreditCard} w="20px" h="20px" />,
    component: <AdminPayments />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminInvoices',
    layout: '/',
    path: '/admin/invoices',
    icon: <Icon as={MdReceipt} w="20px" h="20px" />,
    component: <AdminInvoicesPage />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminPurchaseTerms',
    layout: '/',
    path: '/admin/purchase-terms',
    icon: <Icon as={MdDescription} w="20px" h="20px" />,
    component: <AdminPurchaseTermsPage />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminReviews',
    layout: '/',
    path: '/admin/reviews',
    icon: <Icon as={MdStar} w="20px" h="20px" />,
    component: <AdminReviews />,
    roles: ['admin'],
  },
  {
    name: 'nav.adminSettings',
    layout: '/',
    path: '/admin/settings',
    icon: <Icon as={MdSettings} w="20px" h="20px" />,
    component: <AdminSettings />,
    roles: ['admin'],
  },
];

export default routes;